import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { estoqueApi } from '../../services/api';
import { Material, MovimentacaoEstoqueFormData, TipoMovimentacao } from '../../types';

const MovimentacaoForm: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [materiais, setMateriais] = useState<Material[]>([]);
  const [materialSelecionado, setMaterialSelecionado] = useState<Material | null>(null);

  const [formData, setFormData] = useState<MovimentacaoEstoqueFormData>({
    material_id: 0,
    tipo: 'E',
    quantidade: 0,
    preco_unitario: 0,
    data: new Date().toISOString().split('T')[0],
    observacao: '',
  });

  useEffect(() => {
    loadMateriais();
  }, []);

  const loadMateriais = async () => {
    try {
      const response = await estoqueApi.getMateriais();
      setMateriais(response.results || []);
    } catch (err) {
      console.error('Erro ao carregar materiais:', err);
    }
  };

  const handleMaterialChange = (materialId: number) => {
    const material = materiais.find(m => Number(m.id) === materialId);
    setMaterialSelecionado(material || null);
    setFormData(prev => ({
      ...prev,
      material_id: materialId,
      // Se for entrada, sugerir último preço de compra (se disponível)
      preco_unitario: material && formData.tipo === 'E' && material.preco_ultima_compra
        ? material.preco_ultima_compra
        : prev.preco_unitario,
    }));
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'quantidade' || name === 'preco_unitario'
        ? Number(value)
        : value,
    }));
  };

  const handleTipoChange = (tipo: TipoMovimentacao) => {
    setFormData(prev => ({
      ...prev,
      tipo,
      // Se mudar para entrada e tem material selecionado, sugerir preço (se disponível)
      preco_unitario: tipo === 'E' && materialSelecionado && materialSelecionado.preco_ultima_compra
        ? materialSelecionado.preco_ultima_compra
        : prev.preco_unitario,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validações
    if (!formData.material_id || formData.material_id === 0) {
      setError('Selecione um material');
      return;
    }

    if (formData.quantidade <= 0) {
      setError('Quantidade deve ser maior que zero');
      return;
    }

    if (formData.tipo === 'E' && formData.preco_unitario <= 0) {
      setError('Preço unitário deve ser maior que zero para entradas');
      return;
    }

    // Verificar se tem saldo suficiente para saída
    if (formData.tipo === 'S' && materialSelecionado) {
      if (formData.quantidade > materialSelecionado.saldo_atual) {
        setError(`Saldo insuficiente! Disponível: ${materialSelecionado.saldo_atual} ${materialSelecionado.unidade_display}`);
        return;
      }
    }

    try {
      setLoading(true);
      setError('');

      // Transformar material_id em material para o backend
      const payload: any = {
        material: formData.material_id,
        tipo: formData.tipo,
        quantidade: formData.quantidade,
        preco_unitario: formData.preco_unitario,
        data: formData.data,
        observacao: formData.observacao,
      };

      await estoqueApi.createMovimentacao(payload);
      navigate('/estoque/movimentacoes');
    } catch (err: any) {
      const errorMsg = err.response?.data?.detail ||
                      Object.values(err.response?.data || {}).flat().join(', ') ||
                      'Erro ao registrar movimentação';
      setError(errorMsg);
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const valorTotal = formData.quantidade * formData.preco_unitario;

  return (
    <div className="space-y-4 max-w-4xl mx-auto">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-base font-bold text-gray-900">
            Nova Movimentação de Estoque
          </h1>
          <p className="text-xs text-gray-600">
            Registre entrada ou saída de material
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="bg-white shadow-sm rounded-lg p-4 space-y-4">
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded text-sm">
            {error}
          </div>
        )}

        {/* Tipo de Movimentação */}
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-2">
            Tipo de Movimentação *
          </label>
          <div className="grid grid-cols-2 gap-3">
            <button
              type="button"
              onClick={() => handleTipoChange('E')}
              className={`px-3 py-2 text-sm rounded-lg border-2 font-medium transition-colors ${
                formData.tipo === 'E'
                  ? 'border-green-500 bg-green-50 text-green-700'
                  : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
              }`}
            >
              ↓ Entrada
            </button>
            <button
              type="button"
              onClick={() => handleTipoChange('S')}
              className={`px-3 py-2 text-sm rounded-lg border-2 font-medium transition-colors ${
                formData.tipo === 'S'
                  ? 'border-red-500 bg-red-50 text-red-700'
                  : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
              }`}
            >
              ↑ Saída
            </button>
          </div>
        </div>

        {/* Material */}
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">
            Material *
          </label>
          <select
            name="material_id"
            value={formData.material_id}
            onChange={(e) => handleMaterialChange(Number(e.target.value))}
            required
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value={0}>Selecione um material</option>
            {materiais.map(material => (
              <option key={material.id} value={Number(material.id)}>
                {material.codigo} - {material.nome} (Saldo: {material.saldo_atual} {material.unidade_display})
              </option>
            ))}
          </select>

          {/* Info do Material Selecionado */}
          {materialSelecionado && (
            <div className="mt-2 p-2 bg-gray-50 rounded text-xs">
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <span className="text-gray-600">Saldo atual:</span>
                  <span className="ml-2 font-semibold">
                    {materialSelecionado.saldo_atual} {materialSelecionado.unidade_display}
                  </span>
                </div>
                <div>
                  <span className="text-gray-600">Preço médio:</span>
                  <span className="ml-2 font-semibold">
                    R$ {Number(materialSelecionado.preco_medio_ponderado).toFixed(2)}
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Quantidade e Preço */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Quantidade *
            </label>
            <input
              type="number"
              name="quantidade"
              value={formData.quantidade}
              onChange={handleChange}
              required
              min="0.01"
              step="0.01"
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Preço Unitário {formData.tipo === 'E' && '*'}
            </label>
            <input
              type="number"
              name="preco_unitario"
              value={formData.preco_unitario}
              onChange={handleChange}
              required={formData.tipo === 'E'}
              min="0"
              step="0.01"
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Valor Total
            </label>
            <input
              type="text"
              value={`R$ ${valorTotal.toFixed(2)}`}
              disabled
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md bg-gray-50 text-gray-700 font-semibold"
            />
          </div>
        </div>

        {/* Data */}
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">
            Data *
          </label>
          <input
            type="date"
            name="data"
            value={formData.data}
            onChange={handleChange}
            required
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        {/* Observação */}
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">
            Observação
          </label>
          <textarea
            name="observacao"
            value={formData.observacao}
            onChange={handleChange}
            rows={2}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Informações adicionais sobre esta movimentação"
          />
        </div>

        {/* Botões */}
        <div className="flex justify-end gap-3 pt-3 border-t">
          <button
            type="button"
            onClick={() => navigate('/estoque/movimentacoes')}
            className="px-3 py-2 text-sm border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={loading}
            className={`px-3 py-2 text-sm rounded-md text-white ${
              formData.tipo === 'E'
                ? 'bg-green-600 hover:bg-green-700'
                : 'bg-red-600 hover:bg-red-700'
            } disabled:opacity-50`}
          >
            {loading ? 'Salvando...' : `Registrar ${formData.tipo === 'E' ? 'Entrada' : 'Saída'}`}
          </button>
        </div>
      </form>
    </div>
  );
};

export default MovimentacaoForm;
